import { BlobProvider } from '@react-pdf/renderer';
import { format } from 'date-fns/format';
import { FileText } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDatePicker from '../../components/GenericDatePicker';
import GenericReportPDF from '../../components/GenericReportPDF';
import GenericTable from '../../components/GenericTable';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { BusinessReport } from '../../models/BusinessReport';
import { getAllActiveGameConfigs } from '../../redux/entities/gameEntity';
import { getBusinessReport } from '../../redux/entities/transactionEntity';
import { getAllUsers } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const TotalSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
`;

const TotalItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TotalLabel = styled.span`
  color: #6c757d;
`;

const TotalValue = styled.span<{ positive?: boolean; negative?: boolean }>`
  font-weight: 700;
  color: ${({ positive, negative }) => 
    positive ? '#28a745' : negative ? '#dc3545' : '#212529'};
`;




const DownloadButton = styled(Button)`
  background-color: #4a90e2;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  
  &:hover {
    background-color: #357abd;
  }
`;

const GameBusinessPage: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);

  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();
  const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);
  const aggTran = useAppSelector((state) => state.entities.transactionReducer.businessReports);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const SafeDownloadButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = DownloadButton as any;

  useEffect(() => {
    const fetchGameConfigs = async () => {
      if ((!gameConfigs || gameConfigs.length === 0) && !isLoading && !hasFetched) {
        try {
          setIsLoading(true);
          await dispatch(getAllActiveGameConfigs()).unwrap();
          setHasFetched(true);
        } catch (err) {
          showModalStrip('danger', 'Failed to load game configurations', 5000);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchGameConfigs();
  }, [dispatch, gameConfigs, isLoading, hasFetched, showModalStrip]);

  useEffect(() => {
    dispatch(getAllUsers(0)); // Fetch users if not already available in Redux
  }, [dispatch]);

  const handleFetchTransactions = () => {
    dispatch(getBusinessReport({
      date: format(startDate === null ? new Date() : startDate, "yyyy-MM-dd")
    }));
  }

  const calculateTotals = () => {
    if (!aggTran || aggTran.length === 0) {
      return {
        totalAmount: 0,
        winAmount: 0,
        agentCommission: 0,
        adminCommission: 0,
        profit: 0
      };
    }

    return aggTran.reduce(
      (acc, curr) => ({
        totalAmount: acc.totalAmount + (Number(curr.totalTransaction) || 0),
        winAmount: acc.winAmount + (Number(curr.winAmount) || 0),
        agentCommission: acc.agentCommission + (Number(curr.agentCommission) || 0),
        adminCommission: acc.adminCommission + (Number(curr.adminCommission) || 0),
        profit: acc.profit + (Number(curr.profit) || 0)
      }),
      {
        totalAmount: 0,
        winAmount: 0,
        agentCommission: 0,
        adminCommission: 0,
        profit: 0
      }
    );
  };

  const totals = calculateTotals();

  return (
    <Container>
      <PageHeading>Game Business</PageHeading>

      <TableWrapper>
        <GenericDatePicker
          label='Game Date'
          value={startDate}
          onChange={(date) => setStartDate(date)}
        />

        <GenericButton
          label='Fetch'
          onClick={() => handleFetchTransactions()}
        />

        {aggTran && aggTran.length > 0 &&
          <BlobProvider
            document={
              <GenericReportPDF
                title="Game Business Report"
                reportType="Business Report"
                gameDetails={{
                  gameName: aggTran[0]?.game?.name || 'All Games',
                  gameType: aggTran[0]?.gameType || 'All Types',
                  resultTime: aggTran[0]?.resultTime?.resultTime || 'All Times'
                }}
                dateRange={{
                  startDate: startDate || undefined,
                }}
                columns={[
                  { header: 'Date', field: 'date', render: (row) => format(new Date(row.date), 'dd-MM-yyyy') },
                  { header: 'Game', field: 'game.name', render: (row) => row.game?.name || '-' },
                  { header: 'Result Time', field: 'resultTime.resultTime', render: (row) => row.resultTime?.resultTime || '-' },
                  { header: 'Total Amount', field: 'totalTransaction', render: (row) => `${Number(row.totalTransaction).toFixed(2)}` },
                  { header: 'Win Amount', field: 'winAmount', render: (row) => `${Number(row.winAmount).toFixed(2)}` },
                  { header: 'Agent Commission', field: 'agentCommission', render: (row) => `${Number(row.agentCommission).toFixed(2)}` },
                  { header: 'Admin Commission', field: 'adminCommission', render: (row) => `${Number(row.adminCommission).toFixed(2)}` },
                  {
                    header: 'Profit/Loss',
                    field: 'profit',
                    render: (row) => {
                      const profit = Number(row.profit);
                      return profit >= 0 ? `${profit.toFixed(2)}` : `${profit.toFixed(2)}`;
                    }
                  }
                ]}
                data={[...aggTran].sort((a, b) => {
                  // First sort by date
                  const dateA = new Date(a.date).getTime();
                  const dateB = new Date(b.date).getTime();
                  if (dateA !== dateB) return dateA - dateB;
                  
                  // If dates are the same, sort by game name
                  const gameA = a.game?.name || '';
                  const gameB = b.game?.name || '';
                  if (gameA !== gameB) return gameA.localeCompare(gameB);
                  
                  // If games are the same, sort by result time (convert to Date for proper time comparison)
                  const timeA = a.resultTime?.resultTime ? new Date(`1970-01-01T${a.resultTime.resultTime}`).getTime() : 0;
                  const timeB = b.resultTime?.resultTime ? new Date(`1970-01-01T${b.resultTime.resultTime}`).getTime() : 0;
                  return timeA - timeB;
                })}
                summary={{
                  totalBetAmount: totals.totalAmount,
                  totalWinAmount: totals.winAmount,
                  profitLoss: totals.profit,
                  agentCommission: totals.agentCommission,
                  adminCommission: totals.adminCommission
                }}
              />
            }
          >
            {({ url }) => (
              <SafeDownloadButton
                onClick={() => {
                  if (url) {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `business-report-${format(startDate || new Date(), 'dd-MM-yyyy')}.pdf`;
                    link.click();
                  }
                }}
              >
                <FileText /> Download Report
              </SafeDownloadButton>
            )}
          </BlobProvider>
        }

        <GenericTable
          columns={[
            {
              header: 'Date', field: 'date', render: (rowData: BusinessReport) => {
                return (
                  <span className="single-line-text">
                    {format(new Date(rowData.date), 'dd-MM-yyyy')}
                  </span>
                );
              }
            },
            { header: 'Game', field: 'game.name' },
            { header: 'Result Time', field: 'resultTime.resultTime' },
            {
              header: 'Total Amount', field: 'totalTransaction',
              render: (rowData: any) => Number(rowData.totalTransaction).toFixed(2)
            },
            {
              header: 'Win Amount', field: 'winAmount',
              render: (rowData: any) => Number(rowData.winAmount).toFixed(2)
            },
            {
              header: 'Agent Commission', field: 'agentCommission',
              render: (rowData: any) => Number(rowData.agentCommission).toFixed(2)
            },
            {
              header: 'Admin Commission', field: 'adminCommission',
              render: (rowData: any) => Number(rowData.adminCommission).toFixed(2)
            },
            {
              header: 'Profit/Loss', field: 'profit',
              render: (rowData: any) => {
                const profitLoss = rowData?.profit.toFixed(2); // Replace with actual field for Min value
                const profitLossStyle = profitLoss >= 0 ? { color: 'green' } : { color: 'red' };

                return (
                  <span style={profitLossStyle}>
                    {profitLoss >= 0 ? `+${profitLoss}` : profitLoss}
                  </span>
                );
              }

            },
          ]}
          data={[...aggTran].sort((a, b) => {
            // First sort by date
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            if (dateA !== dateB) return dateA - dateB;
            
            // If dates are the same, sort by game name
            const gameA = a.game?.name || '';
            const gameB = b.game?.name || '';
            if (gameA !== gameB) return gameA.localeCompare(gameB);
            
            // If games are the same, sort by result time (convert to Date for proper time comparison)
            const timeA = a.resultTime?.resultTime ? new Date(`1970-01-01T${a.resultTime.resultTime}`).getTime() : 0;
            const timeB = b.resultTime?.resultTime ? new Date(`1970-01-01T${b.resultTime.resultTime}`).getTime() : 0;
            return timeA - timeB;
          })}
          actions={false} // Don't display the generic edit and delete buttons, we are using custom actions
          pagination={false}
        />

        {aggTran && aggTran.length > 0 && (
          <TotalSection>
            <TotalItem>
              <TotalLabel>Total Amount:</TotalLabel>
              <TotalValue positive={totals.totalAmount >= 0} negative={totals.totalAmount < 0}
              >{totals.totalAmount.toFixed(2)}</TotalValue>
            </TotalItem>
            <TotalItem>
              <TotalLabel>Win Amount:</TotalLabel>
              <TotalValue positive={totals.winAmount >= 0} negative={totals.winAmount < 0}
              >{totals.winAmount >= 0 ? `+${totals.winAmount.toFixed(2)}` : totals.winAmount.toFixed(2)}
              </TotalValue>
            </TotalItem>
            <TotalItem>
              <TotalLabel>Agent Commission:</TotalLabel>
              <TotalValue positive={totals.agentCommission >= 0} negative={totals.agentCommission < 0}
              >{totals.agentCommission >= 0 ? `+${totals.agentCommission.toFixed(2)}` : totals.agentCommission.toFixed(2)}
              </TotalValue>
            </TotalItem>
            <TotalItem>
              <TotalLabel>Admin Commission:</TotalLabel>
              <TotalValue positive={totals.adminCommission >= 0} negative={totals.adminCommission < 0}>
                {totals.adminCommission >= 0 ? `+${totals.adminCommission.toFixed(2)}` : totals.adminCommission.toFixed(2)}
              </TotalValue>
            </TotalItem>
            <TotalItem>
              <TotalLabel>Total Profit/Loss:</TotalLabel>
              <TotalValue positive={totals.profit >= 0} negative={totals.profit < 0}>
                {totals.profit >= 0 ? `+${totals.profit.toFixed(2)}` : totals.profit.toFixed(2)}
              </TotalValue>
            </TotalItem>
          </TotalSection>
        )}
      </TableWrapper>

    </Container>
  );
};

export default GameBusinessPage;
